/**
 * Sentry privacy policy for the browser SDK.
 *
 * Sentry's DPA (§2.2.2) forbids sending special-category data — and on this
 * platform most authenticated screens *are* health / end-of-life content.
 * Replay already masks all text and media; this module adds the two
 * remaining controls:
 *
 *   1. An allow-list of routes where session replay may run at all. Anything
 *      not listed (questionnaire, recordings, workbook, team pages, profiles,
 *      messages…) never records — new routes default to "no replay".
 *   2. Scrubbers for error events and breadcrumbs so URLs, query strings,
 *      request payloads and console output can't carry member content.
 */

// Route prefixes where replay is permitted: public/marketing, auth, legal,
// onboarding, sponsorship/payment, and navigation hubs that show no answers.
export const REPLAY_ALLOWED_PREFIXES = [
  '/login',
  '/register',
  '/verify-email',
  '/verification-pending',
  '/forgot-password',
  '/reset-password',
  '/restore-account',
  '/accept-invitation',
  '/auth/',
  '/sponsor',          // /sponsor/*, /sponsor-a-care-team/*
  '/gift',
  '/payment',
  '/checkout',
  '/onboarding',
  '/welcome',
  '/legal',
  '/privacy',
  '/terms',
  '/care-team-guidelines',
  '/cookie-policy',
  '/recording-consent',
  '/platform-disclaimer',
  '/testing-agreement',
  '/consumer-health-data',
  '/care-fund-terms',
  '/advocacy-terms',
  '/help',
  '/under-construction',
  '/dashboard',
  '/teams',            // team list only — /team/:id is NOT allowed
  '/funding',
  '/notifications',
  '/facilitator-preview',
  '/advocacy-preview', // demo content, no member data
];

/** True when session replay may record on this pathname. */
export function isReplayAllowedPath(pathname) {
  if (typeof pathname !== 'string') return false;
  const path = pathname.split('?')[0].split('#')[0] || '/';
  if (path === '/') return true;
  return REPLAY_ALLOWED_PREFIXES.some((prefix) =>
    prefix.endsWith('/') ? path.startsWith(prefix) : path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}-`),
  );
}

/** Strip query string and fragment — search terms, tokens, invite codes. */
export function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

const REDACTED = '[redacted]';
const SENSITIVE_HEADERS = /^(authorization|cookie|set-cookie|x-csrftoken)$/i;

/**
 * beforeSend: an error event never leaves with member content attached.
 * Keeps what debugging needs (stack, release, route, user id) and drops the
 * rest (payloads, query strings, cookies, email, console arguments).
 */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event;

  if (event.request) {
    event.request.url = scrubUrl(event.request.url);
    event.request.query_string = undefined;
    event.request.data = undefined;
    event.request.cookies = undefined;
    if (event.request.headers) {
      for (const name of Object.keys(event.request.headers)) {
        if (SENSITIVE_HEADERS.test(name)) event.request.headers[name] = REDACTED;
      }
    }
  }

  if (event.user) {
    // An opaque id is enough to count affected members; nothing identifying.
    event.user = event.user.id ? { id: String(event.user.id) } : undefined;
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  }

  // Free-form containers are where serialized state ends up.
  delete event.extra;
  if (event.contexts) delete event.contexts.state;

  return event;
}

/**
 * beforeBreadcrumb: navigation and network breadcrumbs keep their path, lose
 * their query strings and bodies; console breadcrumbs are dropped outright.
 */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  if (breadcrumb.category === 'console') return null;

  const data = breadcrumb.data;
  if (data && typeof data === 'object') {
    if (typeof data.url === 'string') data.url = scrubUrl(data.url);
    if (typeof data.from === 'string') data.from = scrubUrl(data.from);
    if (typeof data.to === 'string') data.to = scrubUrl(data.to);
    delete data.input;
    delete data.body;
    delete data.request_body_size;
    delete data.response_body_size;
  }
  if (typeof breadcrumb.message === 'string' && breadcrumb.category !== 'ui.click') {
    breadcrumb.message = scrubUrl(breadcrumb.message);
  }
  return breadcrumb;
}
